import guitarDb from '@tombatossals/chords-db/lib/guitar.json';
import { chordParserFactory, type MaybeChord, type NormalizedChord } from 'chord-symbol';
import type { Barre, Chord, Finger } from 'svguitar';
import { AChords } from './chords/a.chords';
import { BChords } from './chords/b.chords';
import { CChords } from './chords/c.chords';
import { CSustChords } from './chords/c-sust.chords';
import { DChords } from './chords/d.chords';
import { EChords } from './chords/e.chords';
import { FChords } from './chords/f.chords';
import { FSustChords } from './chords/f-sust.chords';
import { GChords } from './chords/g.chords';
import { GSustChords } from './chords/g-sust.chords';

/**
 * Formas desenhadas à mão, com prioridade sobre a busca automática no
 * chords-db. Muitos acordes "de cifra" (9, add9, sus...) usam voicings
 * simplificados que não seguem a teoria musical estrita e por isso não
 * existem na base automática — aqui é onde essas exceções entram.
 */
const chordOverrides: Record<string, Chord> = {
  ...CChords,
  ...CSustChords,
  ...DChords,
  ...EChords,
  ...FChords,
  ...FSustChords,
  ...GChords,
  ...GSustChords,
  ...AChords,
  ...BChords,
};

type DbPosition = {
  frets: number[];
  fingers: number[];
  baseFret: number;
  barres: number[];
};

type DbChordEntry = {
  key: string;
  suffix: string;
  positions: DbPosition[];
};

const chordsByRoot = (guitarDb as unknown as { chords: Record<string, DbChordEntry[]> })
  .chords;

const parseChord = chordParserFactory();

/**
 * chords-db usa nomes de propriedade "seguros" para os sustenidos
 * (ex: "Csharp" em vez de "C#"), e não tem entradas para bemóis.
 */
const ROOT_TO_DB_KEY: Record<string, string> = {
  C: 'C',
  'B#': 'C',
  'C#': 'Csharp',
  Db: 'Csharp',
  D: 'D',
  'D#': 'Eb',
  Eb: 'Eb',
  E: 'E',
  Fb: 'E',
  F: 'F',
  'E#': 'F',
  'F#': 'Fsharp',
  Gb: 'Fsharp',
  G: 'G',
  'G#': 'Ab',
  Ab: 'Ab',
  A: 'A',
  'A#': 'Bb',
  Bb: 'Bb',
  B: 'B',
  Cb: 'B',
};

/**
 * A "vertical quality" que o chord-symbol devolve (major, minor7, dominant7...)
 * é mapeada para os sufixos usados pelo chords-db, do mais específico pro mais
 * genérico. O primeiro sufixo que existir de fato na base é usado.
 */
const resolveSuffixCandidates = (normalized: NormalizedChord): string[] => {
  const { quality, isSuspended, extensions, alterations, adds } = normalized;
  const topExtension = extensions[extensions.length - 1];
  const alteration = alterations[0];
  const add = adds[0];
  const candidates: string[] = [];

  switch (quality) {
    case 'major':
      if (isSuspended) candidates.push('sus4');
      if (add === '9') candidates.push('add9');
      candidates.push('major');
      break;
    case 'minor':
      if (add === '9') candidates.push('madd9');
      candidates.push('minor');
      break;
    case 'dominant7':
      if (isSuspended) candidates.push('7sus4');
      if (alteration === 'b5') candidates.push('7b5');
      if (alteration === '#5') candidates.push('aug7');
      if (alteration === '#9') candidates.push('7#9');
      if (alteration === 'b9') candidates.push('7b9');
      if (topExtension === '13') candidates.push('13');
      if (topExtension === '11') candidates.push('11');
      if (topExtension === '9') candidates.push('9');
      candidates.push('7');
      break;
    case 'major7':
      if (alteration === 'b5') candidates.push('maj7b5');
      if (alteration === '#5') candidates.push('maj7#5');
      if (topExtension === '13') candidates.push('maj13');
      if (topExtension === '11') candidates.push('maj11');
      if (topExtension === '9') candidates.push('maj9');
      candidates.push('maj7');
      break;
    case 'minor7':
      if (alteration === 'b5') candidates.push('m7b5');
      if (topExtension === '11') candidates.push('m11');
      if (topExtension === '9') candidates.push('m9');
      candidates.push('m7');
      break;
    case 'minorMajor7':
      if (topExtension === '11') candidates.push('mmaj11');
      if (topExtension === '9') candidates.push('mmaj9');
      candidates.push('mmaj7');
      break;
    case 'major6':
      if (add === '9') candidates.push('69');
      candidates.push('6');
      break;
    case 'minor6':
      if (add === '9') candidates.push('m69');
      candidates.push('m6');
      break;
    case 'diminished':
      candidates.push('dim');
      break;
    case 'diminished7':
      candidates.push('dim7', 'dim');
      break;
    case 'augmented':
      candidates.push('aug');
      break;
    default:
      break;
  }

  candidates.push('major');

  return candidates;
};

const dbPositionToShape = (position: DbPosition, title: string): Chord => {
  const fingers: Finger[] = position.frets.map((fret, index) => {
    const stringNumber = 6 - index;

    if (fret === -1) return [stringNumber, 'x'];
    if (fret === 0) return [stringNumber, 0];

    const fingerNumber = position.fingers[index];

    return fingerNumber
      ? [stringNumber, fret, String(fingerNumber)]
      : [stringNumber, fret];
  });

  // O chords-db às vezes marca um fret como "barre" só porque duas ou mais
  // cordas soam nele, mesmo quando são dedos diferentes (não uma pestana de
  // verdade). Só desenhamos a barra quando o mesmo dedo cobre todas elas.
  const isRealBarre = (relativeFret: number) => {
    const fingersAtFret = position.frets
      .map((fret, index) => (fret === relativeFret ? position.fingers[index] : undefined))
      .filter((finger): finger is number => !!finger);

    return (
      fingersAtFret.length > 1 &&
      fingersAtFret.every((finger) => finger === fingersAtFret[0])
    );
  };

  const barres: Barre[] = position.barres.filter(isRealBarre).map((relativeFret) => ({
    fromString: 6,
    toString: 1,
    fret: relativeFret,
  }));

  return {
    fingers,
    barres,
    position: position.baseFret,
    title,
  };
};

const shapeCache = new Map<string, Chord | undefined>();

const findChordShape = (chordName: string): Chord | undefined => {
  const parsed: MaybeChord = parseChord(chordName);
  if (!('normalized' in parsed)) return undefined;

  const dbKey = ROOT_TO_DB_KEY[parsed.normalized.rootNote];
  const entries = dbKey ? chordsByRoot[dbKey] : undefined;
  if (!entries) return undefined;

  const suffixCandidates = resolveSuffixCandidates(parsed.normalized);
  const suffix = suffixCandidates.find((candidate) =>
    entries.some((entry) => entry.suffix === candidate),
  );
  const entry = entries.find((e) => e.suffix === suffix);

  // Posições com baseFret > 1 e cordas soltas (fret 0) não têm o traste
  // zero (a pestana/nut) visível no diagrama, o que deixa os círculos de
  // corda solta com posição errada no svguitar. Preferimos uma posição sem
  // esse conflito, mesmo que seja mais "difícil" de tocar.
  const isDisplayFriendly = (p: DbPosition) => p.baseFret === 1 || !p.frets.includes(0);
  const position = entry?.positions.find(isDisplayFriendly) ?? entry?.positions[0];

  return position ? dbPositionToShape(position, chordName) : undefined;
};

const lookupChordShape = (chordName: string): Chord | undefined => {
  if (chordOverrides[chordName]) return chordOverrides[chordName];

  if (shapeCache.has(chordName)) return shapeCache.get(chordName);

  let shape: Chord | undefined;
  try {
    shape = findChordShape(chordName);
  } catch {
    shape = undefined;
  }

  shapeCache.set(chordName, shape);
  return shape;
};

export const chordShapes: Record<string, Chord> = new Proxy(
  {},
  { get: (_target, prop: string) => lookupChordShape(prop) },
) as Record<string, Chord>;

// preserva o sustenido antes de remover os outros símbolos, senão "F#" e "F"
// (ou "C#m" e "Cm") viram o mesmo id e disputam o mesmo elemento no DOM.
export const getChordId = (chord: string) =>
  chord.replace(/#/g, 'sharp').replace(/[^a-zA-Z0-9]/g, '');
